import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { timeAgo } from "@/lib/format";
import { MarketingPanel } from "@/pages/Marketing";
import { FreeAccessManager } from "@/components/FreeAccessManager";
import { Loader2, Users, FileText, Send, Eye, Camera, CreditCard, Circle, ChevronDown, ChevronRight, Trash2, FlaskConical, Activity, DollarSign, Smartphone, Bell, CalendarClock, Globe, RefreshCw, Megaphone, Sparkles, Check, ListChecks } from "lucide-react";

const pct = (n, d) => `${d ? Math.round((n / d) * 100) : 0}%`;

function Sparkline({ data = [] }) {
  const max = Math.max(1, ...data);
  const n = Math.max(1, data.length - 1);
  const pts = data.map((v, i) => `${(i / n) * 100},${28 - (v / max) * 26}`).join(" ");
  return (
    <svg viewBox="0 0 100 28" preserveAspectRatio="none" className="h-7 w-full">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrendCard({ icon: Icon, label, pack }) {
  const p = pack || { daily: [], today: 0, d7: 0, d30: 0 };
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`trend-${label}`}>
      <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground"><Icon className="h-4 w-4 text-emerald-500" />{label}</div>
      <div className="mt-1 flex items-end justify-between">
        <div className="font-heading text-2xl font-bold">{p.d30}</div>
        <div className="text-[11px] text-muted-foreground">30d</div>
      </div>
      <div className="mt-1 text-emerald-500"><Sparkline data={p.daily} /></div>
      <div className="mt-1 flex justify-between text-[11px] text-muted-foreground"><span>Today {p.today}</span><span>7d {p.d7}</span></div>
      {typeof p.free === "number" && (
        <div className="mt-2 border-t border-border pt-2" data-testid="signups-breakdown">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">By plan · {p.total} accounts</div>
          <div className="grid grid-cols-3 gap-1 text-center">
            <div data-testid="signups-paid"><div className="text-base font-bold text-emerald-500">{p.paid}</div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Paid</div></div>
            <div data-testid="signups-trial"><div className="text-base font-bold text-amber-500">{p.trial}</div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Trial</div></div>
            <div data-testid="signups-free"><div className="text-base font-bold text-foreground">{p.free}</div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Free</div></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 text-center" data-testid={`stat-${label}`}>
      <div className="font-heading text-2xl font-bold">{value}</div>
      <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function FilterStat({ label, value, sub, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-2xl border p-4 text-center transition-colors ${active ? "border-emerald-500 bg-emerald-500/10 ring-1 ring-emerald-500" : "border-border bg-card hover:border-emerald-500/40"}`}
    >
      <div className={`font-heading text-2xl font-bold ${active ? "text-emerald-400" : ""}`}>{value}</div>
      <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </button>
  );
}

function BarList({ items = [] }) {
  const max = Math.max(1, ...items.map((i) => i.n));
  if (items.length === 0) return <p className="text-xs text-muted-foreground">No data yet.</p>;
  return (
    <div className="space-y-1.5">
      {items.map((it) => (
        <div key={it.name} className="flex items-center gap-2 text-xs">
          <span className="w-16 shrink-0 truncate text-muted-foreground">{it.name}</span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${(it.n / max) * 100}%` }} /></div>
          <span className="w-8 shrink-0 text-right font-semibold">{it.n}</span>
        </div>
      ))}
    </div>
  );
}

const STATUS = {
  active: { label: "Active", cls: "bg-emerald-500/15 text-emerald-400" },
  dormant: { label: "Dormant", cls: "bg-amber-500/15 text-amber-400" },
  never: { label: "Never signed in", cls: "bg-slate-500/15 text-slate-400" },
};

const FILTERS = [
  { id: "all", label: "All" },
  { id: "never", label: "Haven't signed in" },
  { id: "sent", label: "Sent a link" },
  { id: "opened", label: "Got an open" },
  { id: "photo", label: "Got a photo" },
];

function FunnelBar({ icon: Icon, label, value, total, prev }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const step = prev != null && prev > 0 ? Math.round((value / prev) * 100) : null;
  const dropped = prev != null && prev > value;
  return (
    <div data-testid={`funnel-${label}`}>
      <div className="flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4 text-emerald-500" /> {label}</span>
        <span className="flex items-center gap-2 font-semibold text-foreground">
          {value} <span className="text-xs text-muted-foreground">· {pct}%</span>
          {step != null && (
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${dropped ? "bg-rose-500/15 text-rose-400" : "bg-emerald-500/15 text-emerald-400"}`}>
              {step}%↳
            </span>
          )}
        </span>
      </div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function TesterRow({ t }) {
  const s = STATUS[t.status] || STATUS.never;
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={`tester-row-${t.email}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold">{t.email}</span>
            {t.is_tester && <span className="shrink-0 rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-sky-400">Tester</span>}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {t.signed_in ? <>Last active {t.last_active ? timeAgo(t.last_active) : "—"}</> : "Invited · not signed in yet"}
          </div>
          {t.signed_in && t.signup_date && (
            <div className="text-xs text-muted-foreground/70" data-testid={`signup-date-${t.email}`}>Joined {timeAgo(t.signup_date)}</div>
          )}
        </div>
        <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-bold ${s.cls}`}>{s.label}</span>
      </div>
      <div className="mt-3 grid grid-cols-5 gap-1 text-center">
        {[["Reports", t.reports], ["Links", t.links], ["Opens", t.opens], ["Photos", t.photos], ["Sent", t.proofs]].map(([k, v]) => (
          <div key={k}>
            <div className={`font-heading text-lg font-bold ${v ? "text-foreground" : "text-muted-foreground/40"}`}>{v}</div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Circle className={`h-2 w-2 ${["trialing", "active"].includes(t.sub_status) ? "fill-emerald-500 text-emerald-500" : t.sub_status === "sandbox" ? "fill-sky-500 text-sky-500" : "fill-slate-500 text-slate-500"}`} />
        {t.sub_status === "active" ? "Subscribed" : t.sub_status === "trialing" ? "On trial" : t.sub_status === "sandbox" ? "Sandbox (try-free)" : "Free — no subscription"}
      </div>
    </div>
  );
}

export default function Testers() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showTests, setShowTests] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [tab, setTab] = useState("overview");
  const [signupFilter, setSignupFilter] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/admin/testers"); setData(data); }
    catch { /* handled by empty state */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => { setRefreshing(true); try { await load(); } finally { setRefreshing(false); } };

  const cleanup = async () => {
    if (!window.confirm("Delete all agent/QA test accounts and their data? This can't be undone. Your @fakeemail.com testers and real users are kept.")) return;
    setCleaning(true);
    try {
      const { data: res } = await api.post("/admin/cleanup-test-accounts");
      await load();
      toast.success(`Removed ${res.deleted} test account${res.deleted === 1 ? "" : "s"}`);
    } catch { toast.error("Cleanup failed — try again"); }
    finally { setCleaning(false); }
  };

  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  const f = data?.funnel || {};
  const all = data?.testers || [];
  const testRows = all.filter((t) => t.is_test);
  const applyFilter = (t) => {
    if (filter === "never") return !t.signed_in;
    if (filter === "sent") return t.links > 0;
    if (filter === "opened") return t.opens > 0;
    if (filter === "photo") return t.photos > 0;
    return true;
  };
  const rows = all.filter((t) => !t.is_test && applyFilter(t));

  // Free = real (non-test) accounts that aren't trialing or paid: sandbox try-free + free viewers.
  const freeRows = all.filter((t) => !t.is_test && !["trialing", "active"].includes(t.sub_status));
  const sandboxCount = freeRows.filter((t) => t.sub_status === "sandbox").length;
  const freePlainCount = freeRows.length - sandboxCount;
  const freeActive = freeRows.filter((t) => t.status === "active").length;

  // Everyone who actually created an account (real, signed-in) — newest first.
  const signupRows = all
    .filter((t) => !t.is_test && t.signed_in)
    .sort((a, b) => (b.signup_date || "").localeCompare(a.signup_date || ""));
  const trialCount = signupRows.filter((t) => t.sub_status === "trialing").length;
  const paidCount = signupRows.filter((t) => t.sub_status === "active").length;
  const shownSignups = signupFilter === "all" ? signupRows : signupRows.filter((t) => t.sub_status === signupFilter);

  const TABS = [
    { id: "overview", label: "Overview", icon: Activity },
    { id: "signups", label: "Signups", icon: Users },
    { id: "marketing", label: "Marketing", icon: Megaphone },
    { id: "free", label: `Free accounts`, icon: Sparkles },
  ];

  return (
    <AppShell title="Analytics" onBack={() => navigate("/dashboard")}>
      <div className="px-4 pb-10 pt-5 sm:px-8" data-testid="testers-page">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="font-heading text-2xl font-bold">Analytics</h1>
            <p className="mt-1 text-sm text-muted-foreground">Private to you. Growth, activation, conversion &amp; who's actually using it.</p>
          </div>
          <button onClick={refresh} disabled={refreshing} data-testid="refresh-btn" aria-label="Refresh" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto" data-testid="analytics-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === t.id ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground hover:text-foreground"}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
              {t.id === "free" && <span className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${tab === t.id ? "bg-white/20" : "bg-background"}`}>{freeRows.length}</span>}
              {t.id === "signups" && <span className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${tab === t.id ? "bg-white/20" : "bg-background"}`}>{signupRows.length}</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
        ) : !data ? (
          <p className="py-16 text-center text-sm text-muted-foreground" data-testid="testers-error">Couldn't load analytics.</p>
        ) : tab === "marketing" ? (
          <div className="mt-5" data-testid="tab-panel-marketing"><MarketingPanel m={data.marketing} /></div>
        ) : tab === "free" ? (
          <div className="mt-5 space-y-4" data-testid="tab-panel-free">
            <FreeAccessManager />
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Free accounts" value={freeRows.length} sub="not paying" />
              <Stat label="Try-free" value={sandboxCount} sub="sandbox" />
              <Stat label="Active 7d" value={freeActive} sub="of free" />
            </div>
            <p className="text-xs text-muted-foreground">
              Everyone on the free side — <b>sandbox</b> try-free visitors and free viewer accounts. They're counted as signups in your funnel just like anyone else. Convert them by nudging into a trial.
            </p>
            <div className="space-y-2" data-testid="free-list">
              {freeRows.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No free accounts yet.</p>}
              {freeRows.map((t) => <TesterRow key={t.id} t={t} />)}
            </div>
          </div>
        ) : tab === "signups" ? (
          <div className="mt-5 space-y-4" data-testid="tab-panel-signups">
            <div className="grid grid-cols-3 gap-3">
              <FilterStat label="Signups" value={signupRows.length} sub="real accounts" active={signupFilter === "all"} onClick={() => setSignupFilter("all")} testId="signup-filter-all" />
              <FilterStat label="On trial" value={trialCount} sub="trialing" active={signupFilter === "trialing"} onClick={() => setSignupFilter("trialing")} testId="signup-filter-trialing" />
              <FilterStat label="Paid" value={paidCount} sub="subscribed" active={signupFilter === "active"} onClick={() => setSignupFilter("active")} testId="signup-filter-active" />
            </div>
            <p className="text-xs text-muted-foreground">
              {signupFilter === "trialing" ? <>Showing <b>{shownSignups.length}</b> on trial — tap a card above to change the view.</>
                : signupFilter === "active" ? <>Showing <b>{shownSignups.length}</b> paid — tap a card above to change the view.</>
                : <>Everyone who created an account, newest first. Tap <b>On trial</b> or <b>Paid</b> to filter.</>}
            </p>
            <div className="space-y-2" data-testid="signups-list">
              {shownSignups.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No accounts here yet.</p>}
              {shownSignups.map((t) => <TesterRow key={t.id} t={t} />)}
            </div>
          </div>
        ) : (
          <div className="mt-5" data-testid="tab-panel-overview">
            {data.pulse && (
              <div className="space-y-4" data-testid="pulse">
                <div className="flex items-center gap-2 text-sm font-semibold"><Activity className="h-4 w-4 text-emerald-500" /> Pulse <span className="font-normal text-muted-foreground">· real users, last 30 days</span></div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <TrendCard icon={Users} label="Signups" pack={data.pulse.trends.signups} />
                  <TrendCard icon={Send} label="Links sent" pack={data.pulse.trends.links_sent} />
                  <TrendCard icon={Eye} label="Opens" pack={data.pulse.trends.opens} />
                  <TrendCard icon={Camera} label="Proofs back" pack={data.pulse.trends.proofs_back} />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <Stat label="DAU" value={data.pulse.active.dau} sub="today" />
                  <Stat label="WAU" value={data.pulse.active.wau} sub="7-day" />
                  <Stat label="MAU" value={data.pulse.active.mau} sub="30-day" />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <Stat label="Open rate" value={`${data.pulse.quality.open_rate}%`} sub={`${data.pulse.quality.links_opened}/${data.pulse.quality.links_sent}`} />
                  <Stat label="Proof back" value={`${data.pulse.quality.proof_back_rate}%`} sub="of sent" />
                  <Stat label="Response" value={data.pulse.quality.median_response_hours == null ? "—" : `${data.pulse.quality.median_response_hours}h`} sub="open→proof" />
                </div>

                <div className="rounded-2xl border border-border bg-card p-5" data-testid="pulse-revenue">
                  <div className="flex items-center gap-2 text-sm font-semibold"><DollarSign className="h-4 w-4 text-emerald-500" /> Revenue</div>
                  <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Stat label="Trials" value={data.pulse.revenue.trials_started} />
                    <Stat label="On trial" value={data.pulse.revenue.trialing_now} />
                    <Stat label="Active" value={data.pulse.revenue.active_subs} />
                    <Stat label="Trial→Paid" value={`${data.pulse.revenue.trial_to_paid_pct}%`} />
                  </div>
                  <div className="mt-3 flex items-center justify-between rounded-xl bg-emerald-500/10 px-4 py-2 text-sm">
                    <span className="text-muted-foreground">Est. MRR</span>
                    <span className="font-heading text-lg font-bold text-emerald-500">${data.pulse.revenue.est_mrr}</span>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-500"><CalendarClock className="h-3.5 w-3.5" /> Trials expiring ≤ 3 days ({data.pulse.revenue.expiring.length})</div>
                    {data.pulse.revenue.expiring.length === 0 ? (
                      <p className="mt-1 text-xs text-muted-foreground">None expiring soon.</p>
                    ) : (
                      <div className="mt-2 space-y-1">
                        {data.pulse.revenue.expiring.map((e) => (
                          <div key={e.email} className="flex items-center justify-between text-xs" data-testid={`expiring-${e.email}`}>
                            <span className="truncate">{e.email}</span>
                            <span className="shrink-0 font-semibold text-amber-500">{e.days_left}d left</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {data.pulse.cohorts.length > 0 && (
                  <div className="rounded-2xl border border-border bg-card p-5" data-testid="pulse-cohorts">
                    <div className="text-sm font-semibold">Weekly signup cohorts</div>
                    <div className="mt-3 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-muted-foreground">
                            <th className="py-1 text-left font-medium">Week</th>
                            <th className="text-right font-medium">Size</th>
                            <th className="text-right font-medium">Created</th>
                            <th className="text-right font-medium">Sent</th>
                            <th className="text-right font-medium">Proof</th>
                            <th className="text-right font-medium">Subbed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.pulse.cohorts.map((c) => (
                            <tr key={c.week} className="border-t border-border">
                              <td className="py-1.5 font-medium">{c.week}</td>
                              <td className="text-right">{c.size}</td>
                              <td className="text-right">{pct(c.created, c.size)}</td>
                              <td className="text-right">{pct(c.sent, c.size)}</td>
                              <td className="text-right">{pct(c.proof, c.size)}</td>
                              <td className="text-right">{pct(c.subscribed, c.size)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-border bg-card p-5" data-testid="pulse-devices">
                    <div className="flex items-center gap-2 text-sm font-semibold"><Smartphone className="h-4 w-4 text-emerald-500" /> Opens by device</div>
                    <div className="mt-3"><BarList items={data.pulse.reliability.devices} /></div>
                    <div className="mt-4 flex items-center gap-2 text-sm font-semibold"><Globe className="h-4 w-4 text-emerald-500" /> By country</div>
                    <div className="mt-3"><BarList items={data.pulse.reliability.countries} /></div>
                  </div>
                  <div className="rounded-2xl border border-border bg-card p-5" data-testid="pulse-push">
                    <div className="flex items-center gap-2 text-sm font-semibold"><Bell className="h-4 w-4 text-emerald-500" /> Notifications</div>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      <Stat label="Push on" value={data.pulse.reliability.push_enabled} sub="users" />
                      <Stat label="Delivery" value={data.pulse.reliability.push_delivery.rate == null ? "—" : `${data.pulse.reliability.push_delivery.rate}%`} sub={`${data.pulse.reliability.push_delivery.ok}/${data.pulse.reliability.push_delivery.ok + data.pulse.reliability.push_delivery.fail}`} />
                    </div>
                    <p className="mt-3 text-[11px] text-muted-foreground">Push delivery accrues from now. Email delivery is tracked in your Resend dashboard.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-5 space-y-3 rounded-2xl border border-border bg-card p-5" data-testid="funnel-card">
              <div className="text-sm font-semibold">Activation funnel <span className="text-muted-foreground">({f.invited} real users)</span></div>
              <FunnelBar icon={Users} label="Signed up" value={f.signed_up || 0} total={f.invited || 0} prev={f.invited || 0} />
              <FunnelBar icon={FileText} label="Created a list" value={f.created_report || 0} total={f.invited || 0} prev={f.signed_up || 0} />
              <FunnelBar icon={Send} label="Sent a link" value={f.sent_link || 0} total={f.invited || 0} prev={f.created_report || 0} />
              <FunnelBar icon={Eye} label="Link opened" value={f.opened || 0} total={f.invited || 0} prev={f.sent_link || 0} />
              <FunnelBar icon={Camera} label="Photo came back" value={f.got_photo || 0} total={f.invited || 0} prev={f.opened || 0} />
              <FunnelBar icon={CreditCard} label="Subscribed" value={f.subscribed || 0} total={f.invited || 0} prev={f.got_photo || 0} />
            </div>

            {data.pulse?.proof_funnel && (
              <div className="mt-5 space-y-3 rounded-2xl border border-border bg-card p-5" data-testid="proof-funnel-card">
                <div className="flex items-center gap-2 text-sm font-semibold"><Camera className="h-4 w-4 text-emerald-500" /> Proof-back funnel <span className="font-normal text-muted-foreground">· per shared link — where workers drop</span></div>
                <FunnelBar icon={Send} label="Link sent" value={data.pulse.proof_funnel.sent} total={data.pulse.proof_funnel.sent} prev={data.pulse.proof_funnel.sent} />
                <FunnelBar icon={Eye} label="Opened it" value={data.pulse.proof_funnel.opened} total={data.pulse.proof_funnel.sent} prev={data.pulse.proof_funnel.sent} />
                <FunnelBar icon={Camera} label="Took a photo" value={data.pulse.proof_funnel.photo} total={data.pulse.proof_funnel.sent} prev={data.pulse.proof_funnel.opened} />
                <FunnelBar icon={ListChecks} label="Marked all items" value={data.pulse.proof_funnel.marked} total={data.pulse.proof_funnel.sent} prev={data.pulse.proof_funnel.photo} />
                <FunnelBar icon={Check} label="Sent proof back" value={data.pulse.proof_funnel.sent_back} total={data.pulse.proof_funnel.sent} prev={data.pulse.proof_funnel.marked} />
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {FILTERS.map((x) => (
                <button
                  key={x.id}
                  data-testid={`filter-${x.id}`}
                  onClick={() => setFilter(x.id)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${filter === x.id ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground hover:text-foreground"}`}
                >
                  {x.label}
                </button>
              ))}
            </div>

            <div className="mt-3 space-y-2" data-testid="testers-list">
              {rows.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No users match this filter.</p>}
              {rows.map((t) => <TesterRow key={t.id} t={t} />)}
            </div>

            {testRows.length > 0 && (
              <div className="mt-6 pb-8" data-testid="test-folder">
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={() => setShowTests((v) => !v)}
                    data-testid="test-folder-toggle"
                    className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
                  >
                    {showTests ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <FlaskConical className="h-4 w-4" /> QA / Test accounts ({testRows.length})
                  </button>
                  <button
                    onClick={cleanup}
                    disabled={cleaning}
                    data-testid="cleanup-tests-btn"
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-rose-500/15 px-3 py-1.5 text-xs font-bold text-rose-500 transition-colors hover:bg-rose-500/25 disabled:opacity-50"
                  >
                    {cleaning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Clean up
                  </button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Kept out of the funnel above. “Clean up” removes agent-generated throwaway accounts (@example.com, @sendproof-test.com, TEST_…). Your @fakeemail.com testers stay.
                </p>
                {showTests && (
                  <div className="mt-3 space-y-2">
                    {testRows.map((t) => <TesterRow key={t.id} t={t} />)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
